import { PhysicalKind, type ClientApp, type ColumnModel } from "target_wasm";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { useExportSheetsStore } from "stores/useExportSheetsStore";
import { buildCategoryLabels } from "@/lib/chipTextColor";
import {
  attachmentColumnIds,
  buildExportColumnMaps,
  TaskType,
  type ExportFileFormat,
  type ToWorkerMessage,
} from "Worker/entities";
import {
  buildTableExportFilename,
  buildViewExportFilename,
} from "Worker/filenames";
import { resolveItem, tableStateRefusal, type CanvasItem } from "./items";

/** Why a table or view (by directory or focus id) may not leave the model, or
 *  null. Every export door asks this, so no menu can skip it. */
export function exportRefusal(clientApp: ClientApp, id: string): string | null {
  const item = resolveItem(clientApp, id);
  if (!item) return "not available";
  const state = tableStateRefusal(item.table.source);
  if (state) return state;
  if (clientApp.peek_model_permissions()?.is_sealed) return "sealed space";
  // D37: an import without reshare rights shows blank cells and sends none out.
  if (
    clientApp.peek_table_permissions(item.table.table_id)?.can_read_values ===
    false
  )
    return "values hidden";
  return null;
}

export function exportItem(id: string, format: ExportFileFormat): void {
  const { clientApp, sendMessageToWorker } = useBoundStore.getState();
  if (!clientApp || exportRefusal(clientApp, id)) return;
  const item = resolveItem(clientApp, id)!;
  sendMessageToWorker(workerMessage(clientApp, item, format));
}

export function openExportSheets(id: string): void {
  const { clientApp } = useBoundStore.getState();
  if (!clientApp || exportRefusal(clientApp, id)) return;
  const item = resolveItem(clientApp, id)!;
  const { table } = item;
  if (item.kind === "table") {
    useExportSheetsStore.getState().show({
      tableId: table.table_id,
      source: {
        kind: "table",
        table_id: table.table_id,
        version: Number(table.version),
      },
      title: table.table_name,
      cellCount: (Number(table.active_rows) + 1) * table.column_count,
    });
    return;
  }
  const serialized = clientApp.get_table_view_serialized(id);
  if (!serialized) return;
  const { view } = item;
  useExportSheetsStore.getState().show({
    tableId: table.table_id,
    source: {
      kind: "view",
      version: Number(view.table_version),
      table_view: JSON.parse(serialized),
    },
    title: view.display_name(),
    tabName: table.table_name,
    cellCount: (view.active_rows + 1) * view.column_count,
  });
}

function workerMessage(
  clientApp: ClientApp,
  item: CanvasItem,
  format: ExportFileFormat,
): ToWorkerMessage {
  const columns = (
    item.kind === "view"
      ? clientApp.get_table_view_columns(item.id)?.view_columns
      : clientApp.get_table_columns(item.id).table_columns
  ) as ColumnModel[];
  const common = {
    ...buildExportColumnMaps(columns),
    categoryLabels: buildCategoryLabels(
      clientApp,
      item.table.table_id,
      columns,
      PhysicalKind.Categorical,
    ),
    attachmentColumns: attachmentColumnIds(columns, PhysicalKind.Attachment),
    format,
  };
  if (item.kind === "table")
    return {
      task: TaskType.ExportTable,
      data: {
        ...common,
        tableID: String(item.table.table_id),
        tableName: buildTableExportFilename(item.table.table_name),
        version: Number(item.table.version),
        totalRows: item.table.active_rows,
      },
    };
  return {
    task: TaskType.ExportView,
    data: {
      ...common,
      viewName: buildViewExportFilename(
        item.table.table_name,
        item.view.display_name(),
      ),
      version: Number(item.view.table_version),
      tableView: JSON.parse(clientApp.get_table_view_serialized(item.id) ?? ""),
      totalRows: item.view.active_rows,
    },
  };
}
